import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiSend, FiRefreshCw, FiFileText, FiBook, FiTarget, FiList } from 'react-icons/fi';
import { post } from 'aws-amplify/api';

// Import components directly
import SessionManager from '../sessions/SessionManager';
import PersonaSelectorWithContext from '../personas/PersonaSelectorWithContext';
import SimpleMarkdownRenderer from './SimpleMarkdownRenderer';
import UserAvatar from '../common/UserAvatar';
// import CustomerSelectorForChat from "./CustomerSelectorForChat";
import { CustomerProvider } from '../../contexts/CustomerContext';
import { browseCustomers } from '../../utils/customerApi';
import { openDocument, hasDocumentUrl } from '../../utils/documentUtils';
import { useAuth } from '../../hooks/useAuth';

// Utility function to extract clean text from message content
const extractCleanText = (content: string): string => {
  // Remove XML-like tags and extract just the text content
  const textMatch = content.match(/<text>(.*?)<\/text>/s);
  if (textMatch) {
    return textMatch[1].trim();
  }

  // Also try to remove any other XML-like tags as fallback
  const cleanedContent = content
    .replace(/<answer_part>.*?<\/answer_part>/gs, '')
    .replace(/<sources>.*?<\/sources>/gs, '')
    .replace(/<[^>]*>/g, '')
    .trim();

  // If we cleaned something, return it, otherwise return original
  return cleanedContent || content;
};

// Types
interface QNavChatInterfaceProps {
  initialPersona?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  resources?: Resource[];
  citations?: Citation[];
}

interface Citation {
  generatedResponsePart: {
    textResponsePart: {
      span: {
        start: number;
        end: number;
      };
      text: string;
    };
  };
  retrievedReferences: {
    content: {
      text: string;
      type: string;
    };
    location: {
      s3Location?: {
        uri: string;
      };
      type: string;
    };
    metadata: {
      'x-amz-bedrock-kb-source-uri'?: string;
      'x-amz-bedrock-kb-chunk-id'?: string;
      'x-amz-bedrock-kb-data-source-id'?: string;
    };
  }[];
}

interface Resource {
  title: string;
  link: string;
  type: 'document' | 'video' | 'template' | 'tool';
}

export const QNavChatInterface: React.FC<QNavChatInterfaceProps> = ({ initialPersona }) => {
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState<string | null>(urlSessionId || null);
  const [persona, setPersona] = useState<string>(initialPersona || 'advisor');
  const [selectedPersona, setSelectedPersona] = useState<string>(initialPersona || 'advisor');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Add a callback to refresh session list when temp session becomes real
  const [sessionListRefreshCallback, setSessionListRefreshCallback] = useState<(() => void) | null>(null);

  const handleSessionListRefresh = useCallback((callback: () => void) => {
    setSessionListRefreshCallback(() => callback);
  }, []);

  // Context selection state
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedCustomerName, setSelectedCustomerName] = useState<string | null>(null);
  const [selectedEngagementId, setSelectedEngagementId] = useState<string | null>(null);
  const [availableCustomers, setAvailableCustomers] = useState<any[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Load user's assigned customers
  useEffect(() => {
    loadUserCustomers();
  }, []);

  const loadUserCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const customers = await browseCustomers();
      setAvailableCustomers(customers);
    } catch (error) {
      console.error('Error loading customers:', error);
      setAvailableCustomers([]);
    } finally {
      setLoadingCustomers(false);
    }
  };
  const [selectedEngagementName, setSelectedEngagementName] = useState<string | null>(null);
  const [customerNotFoundMessage, setCustomerNotFoundMessage] = useState<string | null>(null);
  const [isAutoSending, setIsAutoSending] = useState(false);

  // Auto-start chat from analytics email - runs after customers are loaded
  useEffect(() => {
    const handleAnalyticsAutoStart = async () => {
      console.log('Analytics auto-start check:', { 
        availableCustomersCount: availableCustomers.length, 
        loadingCustomers 
      });
      
      // Only run if customers are loaded and we have URL parameters
      if (availableCustomers.length === 0 || loadingCustomers) {
        console.log('Skipping auto-start: customers not ready');
        return;
      }
      
      // Check for analytics auto-start parameters
      const urlParams = new URLSearchParams(window.location.search);
      const source = urlParams.get('source');
      const customer = urlParams.get('customer');
      const users = urlParams.get('users');
      const growth = urlParams.get('growth');
      const autoStart = urlParams.get('auto_start');

      console.log('URL parameters:', { source, customer, users, growth, autoStart });

      if (source === 'analytics_email' && autoStart === 'true') {
        console.log('Analytics email detected, attempting customer auto-selection');
        
        // Try to auto-select customer if provided
        if (customer) {
          console.log('Attempting to auto-select customer:', customer);
          await handleCustomerAutoSelection(customer);
        } else {
          console.log('No customer parameter provided');
        }

        // Generate analytics message with structured insights and explicit persona context
        let analyticsMessage = `I'm a CSM and just received analytics data for ${customer || 'our customer'}. Based on these metrics, what strategic actions should I take?\n\n`;

        // Parse metrics
        const usersCount = users ? parseInt(users) : 0;
        const growthRate = growth ? parseFloat(growth) : 0;

        // Add dynamic insights as bullet points
        if (growthRate < 0) {
          analyticsMessage += `• **T4W Active Users**: ${usersCount} users (${growthRate}% decline this week)\n`;
          analyticsMessage += `• **Trend Alert**: Negative growth requires immediate attention\n`;
          analyticsMessage += `• **Risk Level**: High - user engagement declining\n`;
          analyticsMessage += `• **Priority**: Investigate root causes and implement retention strategies\n`;
        } else if (growthRate === 0) {
          analyticsMessage += `• **T4W Active Users**: ${usersCount} users (0% growth - stagnant)\n`;
          analyticsMessage += `• **Growth Status**: Flat adoption with no momentum\n`;
          analyticsMessage += `• **Opportunity**: Re-engagement strategies needed\n`;
          analyticsMessage += `• **Focus Area**: Identify and remove adoption barriers\n`;
        } else {
          analyticsMessage += `• **T4W Active Users**: ${usersCount} users (+${growthRate}% growth this week)\n`;
          analyticsMessage += `• **Growth Momentum**: Positive trajectory maintained\n`;
          analyticsMessage += `• **Performance**: ${growthRate >= 10 ? 'Strong double-digit growth' : 'Steady growth pattern'}\n`;
          analyticsMessage += `• **Status**: ${usersCount > 300 ? 'Large-scale deployment' : usersCount > 100 ? 'Mid-scale adoption' : 'Early-stage adoption'} with ${growthRate >= 5 ? 'excellent' : 'moderate'} momentum\n`;
        }

        // Create a proper session ID
        const newSessionId = `analytics-${Date.now()}`;

        // Set up new session with welcome message
        const welcomeMessage: Message = {
          id: `welcome-${Date.now()}`,
          role: 'assistant',
          content: getWelcomeMessage(persona),
          timestamp: new Date(),
        };

        // Set session state
        setMessages([welcomeMessage]);
        setSessionId(newSessionId);

        // Save session to localStorage - messages cache
        localStorage.setItem(`bedrock-messages-${newSessionId}`, JSON.stringify([welcomeMessage]));

        // Save session to localStorage - session cache (using correct structure for main page)
        const existingSessions = JSON.parse(localStorage.getItem('bedrock-sessions') || '[]');
        const currentTime = new Date().toISOString();
        const newSession = {
          sessionId: newSessionId,
          name: `Analytics Chat - ${customer || 'Customer'}`,
          persona: persona,
          status: 'active',
          createdAt: currentTime,
          lastActivity: currentTime,
          messageCount: 1,
          firstMessage: `Analytics data for ${customer || 'Customer'}`,
        };

        const updatedSessions = [newSession, ...existingSessions];
        localStorage.setItem('bedrock-sessions', JSON.stringify(updatedSessions));

        // Set the input and trigger auto-send
        setInput(analyticsMessage);

        // Auto-send the message after state updates
        setTimeout(() => {
          setIsAutoSending(true);
          handleSendMessage();
        }, 1000); // Longer delay to ensure state is updated

        // Clean up URL parameters to avoid re-triggering
        const url = new URL(window.location.href);
        ['source', 'customer', 'users', 'growth', 'auto_start'].forEach((param) => url.searchParams.delete(param));
        window.history.replaceState({}, document.title, url.toString());
      }
    };

    // Only run auto-start if we don't already have a session
    if (!sessionId && !urlSessionId) {
      handleAnalyticsAutoStart();
    }
  }, [persona, sessionId, urlSessionId, availableCustomers, loadingCustomers]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle customer auto-selection from analytics email
  const handleCustomerAutoSelection = async (customerName: string) => {
    console.log('handleCustomerAutoSelection called with:', customerName);
    console.log('Available customers:', availableCustomers.map(c => ({ id: c.customerId, name: c.name })));
    
    try {
      // Try to find exact match first
      let matchedCustomer = availableCustomers.find(c => 
        c.name?.toLowerCase() === customerName.toLowerCase()
      );
      console.log('Exact match result:', matchedCustomer);

      // If no exact match, try partial match
      if (!matchedCustomer) {
        console.log('No exact match, trying partial match');
        matchedCustomer = availableCustomers.find(c => 
          c.name?.toLowerCase().includes(customerName.toLowerCase()) ||
          customerName.toLowerCase().includes(c.name?.toLowerCase())
        );
        console.log('Partial match result:', matchedCustomer);
      }

      if (matchedCustomer) {
        console.log('Customer matched! Auto-selecting:', matchedCustomer);
        // Customer found and accessible - auto-select it
        setSelectedCustomerId(matchedCustomer.customerId);
        setSelectedCustomerName(matchedCustomer.name);
        console.log('Customer selection updated:', { 
          selectedCustomerId: matchedCustomer.customerId, 
          selectedCustomerName: matchedCustomer.name 
        });
        
        // Clear any previous not found message
        setCustomerNotFoundMessage(null);
        
        // Show success message
        setMessages(prev => [...prev, {
          id: `auto-select-${Date.now()}`,
          content: `✅ Automatically selected customer: **${matchedCustomer.name}** based on your analytics email.`,
          role: 'assistant',
          timestamp: new Date(),
          sessionId: sessionId || 'temp'
        }]);
      } else {
        console.log('No customer match found, proceeding with generic chat');
        // Show message near customer dropdown
        setCustomerNotFoundMessage(`Customer "${customerName}" from analytics email not available. Contact support: q-csx+q-navigator-prod@amazon.com`);
      }
    } catch (error) {
      console.error('Error in customer auto-selection:', error);
      setMessages(prev => [...prev, {
        id: `auto-select-error-${Date.now()}`,
        content: `❌ Error trying to auto-select customer "${customerName}". Please select a customer manually from the dropdown.`,
        role: 'assistant',
        timestamp: new Date(),
        sessionId: sessionId || 'temp'
      }]);
    }
  };

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load session from URL if provided
  useEffect(() => {
    if (urlSessionId) {
      loadSession(urlSessionId);
    }
  }, [urlSessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get welcome message based on persona and selected context
  const getWelcomeMessage = (personaType: string): string => {
    let baseMessage = '';

    switch (personaType) {
      case 'advisor':
        baseMessage =
          "Hello, I'm your Customer Success Advocate. I can help you with change management strategies, governance frameworks, and adoption planning for your enterprise transformation journey.";
        break;
      case 'partner':
        baseMessage =
          "Welcome, partner. I'm here to provide you with enablement tools, frameworks, and resources to help you deliver successful transformation projects for your clients.";
        break;
      case 'tam':
        baseMessage =
          "Hello, I'm your TAM/SA assistant. I can help you with automated health monitoring, standardized playbooks, and customer engagement strategies.";
        break;
      case 'csm':
        baseMessage =
          "Welcome, I'm your Account CSM assistant. I can help you with strategic guidance and oversight to ensure customer success outcomes.";
        break;
      default:
        baseMessage = "Hello, I'm your Navigator Chat.";
    }

    // Add context-specific information if available
    if (selectedCustomerName && selectedEngagementName) {
      return `${baseMessage} I see you're focusing on the "${selectedEngagementName}" engagement for ${selectedCustomerName}. How can I assist you with this specific engagement today?`;
    } else if (selectedCustomerName) {
      return `${baseMessage} I see you're focusing on ${selectedCustomerName}. How can I assist you with this customer today?`;
    } else {
      return `${baseMessage} How can I assist you today?`;
    }
  };

  // Create a new session (currently unused but may be needed)
  /*
  const createSession = async () => {
    setIsLoading(true);
    try {
      // Get user ID from Cognito
      const currentUser = await getCurrentUser();
      const userId = currentUser.username || currentUser.userId;

      // Option C: No manual session creation needed
      // Sessions are created automatically on first message
      const tempSessionId = `new-${Date.now()}`;
      const response = {
        sessionId: tempSessionId,
        createdAt: new Date().toISOString(),
        name: `New ${selectedPersona} Conversation`
      };
            engagementId: selectedEngagementId,
            engagementName: selectedEngagementName
          }
        },
      });

      if (response.sessionId) {
        setSessionId(response.sessionId);
        setPersona(selectedPersona);

        // Add welcome message
        const welcomeMessage: Message = {
          id: `welcome-${Date.now()}`,
          role: "assistant",
          content: getWelcomeMessage(selectedPersona),
          timestamp: new Date(),
        };
        setMessages([welcomeMessage]);
      }
    } finally {
      setIsLoading(false);
    }
  };
  */

  // Load an existing session
  const loadSession = async (sessionIdToLoad: string, sessionPersona?: string, isNewSession: boolean = false) => {
    setIsLoading(true);
    try {
      // If persona is provided, update it
      if (sessionPersona) {
        setPersona(sessionPersona);
      }

      // Get session details to determine the persona if not provided
      if (!sessionPersona) {
        try {
          // Option C: No session details fetching needed
          // Use the persona from the session parameter or default

          if (sessionPersona) {
            setPersona(sessionPersona);
          }
        } catch (err) {
          console.error('Error getting session details:', err);
        }
      }

      // For new sessions, just set up with welcome message
      if (isNewSession) {
        const welcomeMessage: Message = {
          id: `welcome-${Date.now()}`,
          role: 'assistant',
          content: getWelcomeMessage(sessionPersona || persona),
          timestamp: new Date(),
        };
        setMessages([welcomeMessage]);
        setSessionId(sessionIdToLoad);
        setIsLoading(false);
        return;
      }

      // Check for cached messages first (for existing sessions)
      const cachedMessages = localStorage.getItem(`bedrock-messages-${sessionIdToLoad}`);
      if (cachedMessages) {
        const parsedMessages = JSON.parse(cachedMessages);
        setMessages(parsedMessages);
        setSessionId(sessionIdToLoad);
        setIsLoading(false);
        return;
      }

      // Option C: No message history fetching needed
      // Bedrock Agent Memory handles conversation context automatically

      if (isNewSession) {
        const welcomeMessage: Message = {
          id: `welcome-${Date.now()}`,
          role: 'assistant',
          content: getWelcomeMessage(sessionPersona || persona),
          timestamp: new Date(),
        };
        setMessages([welcomeMessage]);
      } else {
        // For existing sessions, start with empty messages
        // Context will be maintained by Bedrock Agent Memory
        setMessages([]);
      }

      setSessionId(sessionIdToLoad);
    } catch (err) {
      console.error('Error loading session:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle persona selection
  const handlePersonaSelect = (selectedPersonaType: string) => {
    setSelectedPersona(selectedPersonaType);
    // Navigate to the new persona URL
    navigate(`/dashboard/chat/${selectedPersonaType}`);
  };

  // Handle context selection
  const handleContextSelect = (
    customerId?: string,
    customerName?: string,
    engagementId?: string,
    engagementName?: string,
  ) => {
    if (customerId) setSelectedCustomerId(customerId);
    if (customerName) setSelectedCustomerName(customerName);
    if (engagementId) setSelectedEngagementId(engagementId);
    if (engagementName) setSelectedEngagementName(engagementName);
  };

  // Handle start chat button click
  const handleStartChat = () => {
    createSessionWithContext(
      selectedPersona,
      selectedCustomerId || undefined,
      selectedCustomerName || undefined,
      selectedEngagementId || undefined,
      selectedEngagementName || undefined,
    );
  };

  // Create a new session with a specific persona and context
  const createSessionWithContext = async (
    personaType: string,
    customerId?: string,
    customerName?: string,
    engagementId?: string,
    engagementName?: string,
  ) => {
    setIsLoading(true);
    try {
      // Option C: No manual session creation needed
      // Sessions are created automatically on first message
      const tempSessionId = `new-${Date.now()}`;
      const response = {
        sessionId: tempSessionId,
        createdAt: new Date().toISOString(),
        name: `New ${personaType} Conversation`,
      };

      if (response.sessionId) {
        setSessionId(response.sessionId);
        setPersona(personaType);

        // Add welcome message
        const welcomeMessage: Message = {
          id: `welcome-${Date.now()}`,
          role: 'assistant',
          content: getWelcomeMessage(personaType),
          timestamp: new Date(),
        };
        setMessages([welcomeMessage]);
      }
    } catch (err) {
      console.error('Error creating session:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle session selection
  const handleSelectSession = (selectedSessionId: string, sessionPersona: string, isNewSession: boolean = false) => {
    loadSession(selectedSessionId, sessionPersona, isNewSession);
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInput(e.target.value);
  };

  // Handle key press (Enter to send, Ctrl+Enter for new line)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (e.ctrlKey || e.metaKey) {
        // Ctrl+Enter or Cmd+Enter: send message
        e.preventDefault();
        if (input.trim() !== '' && !isLoading) {
          handleSendMessage();
        }
      } else if (!e.shiftKey) {
        // Regular Enter: send message (existing behavior)
        e.preventDefault();
        if (input.trim() !== '' && !isLoading) {
          handleSendMessage();
        }
      }
      // Shift+Enter: allow new line (default behavior)
    }
  };

  // Send message
  const handleSendMessage = async () => {
    // Allow auto-send even if input appears empty due to state timing
    if (!isAutoSending && (input.trim() === '' || isLoading)) return;
    if (isAutoSending && isLoading) return;

    // Reset auto-send flag
    if (isAutoSending) {
      setIsAutoSending(false);
    }

    // Add user message to chat
    const userMessage: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);

    // Cache the updated messages
    if (sessionId) {
      localStorage.setItem(`bedrock-messages-${sessionId}`, JSON.stringify(updatedMessages));
    }

    const sentMessage = input;
    setInput('');
    setIsLoading(true);

    try {
      // Send message to API - Option C simplified approach
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();

      const requestPayload = {
        message: sentMessage,
        persona,
        // Only include sessionId if it's a valid Bedrock session ID (not a temporary one)
        ...(sessionId && !sessionId.startsWith('temp-') && !sessionId.startsWith('new-') && { sessionId }),
        // Include context information if available
        context: {
          customerId: selectedCustomerId,
          customerName: selectedCustomerName,
          engagementId: selectedEngagementId,
          engagementName: selectedEngagementName,
        },
        includeEngagementDocuments: true,
      };

      const response = await post({
        apiName: 'api',
        path: '/api/chat',
        options: {
          body: requestPayload,
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        },
      }).response;

      const data = (await response.body.json()) as any;

      // Format and add assistant response
      if (data) {
        // Update session ID from response (Option C auto-session management)
        if (data.sessionId && data.sessionId !== sessionId) {
          // Handle session ID change - this can happen when backend creates a new session
          if (sessionId) {
            if (sessionId.startsWith('temp-') || sessionId.startsWith('new-')) {
              // Remove temporary session from cache
              const existingSessions = JSON.parse(localStorage.getItem('bedrock-sessions') || '[]');
              const filteredSessions = existingSessions.filter((s: any) => s.sessionId !== sessionId);

              // Add real session to cache
              const realSession = {
                sessionId: data.sessionId,
                persona,
                name: `${persona.charAt(0).toUpperCase() + persona.slice(1)} Conversation`,
                createdAt: new Date().toISOString(),
                lastActivity: new Date().toISOString(),
              };

              const updatedSessions = [realSession, ...filteredSessions];
              localStorage.setItem('bedrock-sessions', JSON.stringify(updatedSessions));

              // Trigger session list refresh if callback is available
              if (sessionListRefreshCallback) {
                sessionListRefreshCallback();
              }

              // Remove temporary messages cache
              localStorage.removeItem(`bedrock-messages-${sessionId}`);
            } else {
              // Remove old session messages cache since we're moving to a new session
              localStorage.removeItem(`bedrock-messages-${sessionId}`);

              // Start fresh with just the current user message and upcoming assistant response
              // The updatedMessages already contains the user message, so we'll continue with that
            }
          }

          setSessionId(data.sessionId);
        }

        const assistantMessage: Message = {
          id: data.messageId || `assistant-${Date.now()}`,
          role: 'assistant',
          content: data.content || '',
          timestamp: new Date(),
          resources: data.resources || [],
          citations: data.citations || [],
        };

        const finalMessages = [...updatedMessages, assistantMessage];
        setMessages(finalMessages);

        // Update the cache with the assistant's response using the current sessionId
        const currentSessionId = data.sessionId || sessionId;
        if (currentSessionId) {
          localStorage.setItem(`bedrock-messages-${currentSessionId}`, JSON.stringify(finalMessages));
        }
      }
    } catch (err) {
      console.error('Error sending message:', err);

      // Add error message
      const errorMessage: Message = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: 'Sorry, there was an error processing your request. Please try again.',
        timestamp: new Date(),
      };

      const finalMessages = [...updatedMessages, errorMessage];
      setMessages(finalMessages);

      // Update the cache with the error message
      if (sessionId) {
        localStorage.setItem(`bedrock-messages-${sessionId}`, JSON.stringify(finalMessages));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Get resource icon based on type
  const getResourceIcon = (type: string) => {
    switch (type) {
      case 'document':
        return <FiFileText className="h-4 w-4" />;
      case 'video':
        return <FiBook className="h-4 w-4" />;
      case 'template':
        return <FiList className="h-4 w-4" />;
      case 'tool':
        return <FiTarget className="h-4 w-4" />;
      default:
        return <FiFileText className="h-4 w-4" />;
    }
  };

  // Get persona badge class
  const getPersonaBadgeClass = () => {
    switch (persona) {
      case 'advisor':
        return 'bg-primary-600';
      case 'partner':
        return 'bg-secondary-600';
      case 'tam':
        return 'bg-accent-600';
      case 'csm':
        return 'bg-primary-600';
      default:
        return 'bg-primary-600';
    }
  };

  // Get persona name
  const getPersonaName = () => {
    switch (persona) {
      case 'advisor':
        return 'Advisor';
      case 'partner':
        return 'Partner';
      case 'tam':
        return 'TAM';
      case 'csm':
        return 'Account CSM';
      default:
        return 'Account CSM';
    }
  };

  // Handle prompt suggestion click
  const handlePromptSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
    // Focus the textarea
    const textarea = document.querySelector('textarea');
    if (textarea) {
      textarea.focus();
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 flex flex-col">
        {!sessionId ? (
          // Session selection or creation view
          <div className="h-full flex flex-col items-center justify-center p-6 bg-background">
            <div className="w-full max-w-4xl bg-surface rounded-qnav shadow-brand-card p-4 border border-secondary-200">
              <h2 className="text-2xl font-semibold text-center mb-6">Welcome to Navigator Chat</h2>

              {/* Persona selection with context */}
              <div className="mb-8">
                <PersonaSelectorWithContext
                  onSelect={handlePersonaSelect}
                  onContextSelect={handleContextSelect}
                  selectedPersona={selectedPersona || 'csm'}
                  onStartChat={handleStartChat}
                />
              </div>

              {/* Session management */}
              <div className="border-t border-secondary-200 pt-6 mt-6">
                <SessionManager
                  onSelectSession={handleSelectSession}
                  currentSessionId={sessionId || undefined}
                  onSessionListRefresh={handleSessionListRefresh}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Chat header - sticky with negative top margin to connect with main header */}
            <div className="bg-brand-gradient p-2 border-b border-secondary-300 flex items-center justify-between sticky top-0 z-10 shadow-md mt-[0px] mx-[-1.5rem] px-6">
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => (window.location.href = '/dashboard/chat/csm')}
                  className="text-white hover:text-secondary-200 transition-colors p-1 rounded-full hover:bg-white/10"
                  title="Back to Chat Home"
                  aria-label="Back to Chat Home"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <div>
                  <h2 className="text-xl font-semibold text-white">Transformation Navigator Chat</h2>
                  <p className="text-secondary-200 text-sm">
                    {selectedCustomerName && selectedEngagementName
                      ? `${selectedCustomerName} > ${selectedEngagementName}`
                      : selectedCustomerName
                        ? `${selectedCustomerName}`
                        : 'Get prescriptive guidance for change management, governance, and adoption strategies'}
                  </p>
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-white ${getPersonaBadgeClass()}`}>{getPersonaName()}</div>
            </div>

            {/* Messages container */}
            <div className="flex-1 overflow-y-auto p-4 space-y-5 bg-gradient-to-b from-slate-100/60 to-slate-200/40">
              {messages.map((message, index) => (
                <div
                  key={message.id}
                  className={`flex flex-col sm:flex-row ${
                    message.role === 'user' ? 'justify-end' : 'justify-start'
                  } ${index === messages.length - 1 ? 'animate-slideIn' : ''} gap-4 sm:gap-2`}
                >
                  {/* User Avatar - only for user messages */}
                  {message.role === 'user' && (
                    <div className="flex-shrink-0 sm:order-2 order-1 self-end mb-1">
                      <UserAvatar username={user?.username} size="lg" className="shadow-sm" />
                    </div>
                  )}

                  {/* Assistant Avatar - only for assistant messages */}
                  {message.role === 'assistant' && (
                    <div className="flex-shrink-0 sm:order-1 order-1">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center font-medium shadow-sm">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                        </svg>
                      </div>
                    </div>
                  )}

                  {/* Main chat bubble */}
                  <div
                    className={`${
                      message.role === 'user'
                        ? 'max-w-[75%] sm:order-1 order-2'
                        : 'w-full max-w-full lg:max-w-[85%] sm:order-2 order-2 group'
                    } rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 relative ${
                      message.role === 'user'
                        ? 'bg-gradient-to-r from-primary-700 to-primary-600 text-white user-message-tail ring-1 ring-primary-400/20 px-3 py-2'
                        : 'bg-white border border-secondary-200/60 sm:ml-1 assistant-message-tail hover:border-secondary-300/60 text-gray-900 p-4'
                    }`}
                  >
                    <div
                      className={`flex items-center justify-between text-sm font-medium ${
                        message.role === 'user' ? 'mb-1' : 'mb-1.5'
                      }`}
                    >
                      <div className="flex items-center">
                        {message.role === 'user' ? (
                          <span className="flex items-center">
                            You{' '}
                            <span className="ml-2 text-xs text-white/60">
                              {new Date(message.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </span>
                        ) : (
                          <span className="flex items-center text-gray-700">
                            Navigator Chat{' '}
                            <span className="ml-2 text-xs opacity-70">
                              {new Date(message.timestamp).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </span>
                        )}
                      </div>

                      {/* Copy button for assistant messages */}
                      {message.role === 'assistant' && (
                        <button
                          onClick={async () => {
                            const cleanText = extractCleanText(message.content);
                            try {
                              await navigator.clipboard.writeText(cleanText);
                              setCopiedMessageId(message.id);
                              setTimeout(() => setCopiedMessageId(null), 2000);
                            } catch (err) {
                              console.error('Failed to copy text:', err);
                            }
                          }}
                          className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md hover:bg-secondary-100 transition-all duration-200 text-secondary-500 hover:text-secondary-700"
                          title={copiedMessageId === message.id ? 'Copied!' : 'Copy message'}
                          aria-label="Copy message to clipboard"
                        >
                          {copiedMessageId === message.id ? (
                            <svg
                              className="w-4 h-4 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                          )}
                        </button>
                      )}
                    </div>
                    <SimpleMarkdownRenderer content={message.content} isUserMessage={message.role === 'user'} />
                  </div>

                  {/* Sources panel - only for assistant messages with citations or resources */}
                  {message.role === 'assistant' &&
                    ((message.citations && message.citations.length > 0) ||
                      (message.resources && message.resources.length > 0)) && (
                      <div className="w-full sm:max-w-sm sm:order-3 order-1 mt-4 sm:mt-8">
                        {/* Citations */}
                        {message.citations && message.citations.length > 0 && (
                          <div
                            className="bg-gradient-to-br from-secondary-50/90 to-secondary-100/70 rounded-xl p-3 border border-secondary-200/50 shadow-lg hover:shadow-xl hover:shadow-secondary-200/30 transition-all duration-300 ease-out backdrop-blur-md animate-fadeIn"
                            role="complementary"
                            aria-label="Document sources and references"
                            style={{
                              animationDelay: '200ms',
                              animationFillMode: 'both',
                            }}
                          >
                            <div className="text-sm font-semibold text-secondary-800 mb-2 flex items-center justify-between animate-fadeIn">
                              <div className="flex items-center">
                                <FiFileText className="h-4 w-4 mr-1.5 text-secondary-700" aria-hidden="true" />
                                <span className="tracking-wide text-secondary-800">Sources</span>
                                <span className="ml-2 text-xs font-medium text-secondary-600 bg-secondary-200/50 px-2 py-0.5 rounded-full">
                                  {
                                    Array.from(
                                      new Set(
                                        message.citations.flatMap((citation) =>
                                          citation.retrievedReferences
                                            .map((ref) => {
                                              const s3Uri = ref.location?.s3Location?.uri;
                                              return s3Uri
                                                ? s3Uri
                                                    .split('/')
                                                    .pop()
                                                    ?.replace(/\.(docx?|pdf|txt)$/i, '')
                                                : null;
                                            })
                                            .filter(Boolean),
                                        ),
                                      ),
                                    ).length
                                  }
                                </span>
                              </div>
                            </div>
                            <div className="space-y-1.5">
                              {(() => {
                                // Create a map to deduplicate references by document name
                                const uniqueReferences = new Map();

                                // Collect all references from all citations
                                message.citations.forEach((citation) => {
                                  citation.retrievedReferences.forEach((reference) => {
                                    const s3Uri = reference.location?.s3Location?.uri;
                                    if (s3Uri) {
                                      // Extract document name from S3 URI
                                      const documentName =
                                        s3Uri
                                          .split('/')
                                          .pop()
                                          ?.replace(/\.(docx?|pdf|txt)$/i, '') || 'Unknown Document';

                                      // Use document name as unique key to avoid duplicates
                                      if (!uniqueReferences.has(documentName)) {
                                        uniqueReferences.set(documentName, {
                                          ...reference,
                                          documentName,
                                          s3Uri,
                                        });
                                      } else {
                                        // If document already exists, merge content if it's longer/better
                                        const existing = uniqueReferences.get(documentName);
                                        if (
                                          reference.content?.text &&
                                          (!existing.content?.text ||
                                            reference.content.text.length > existing.content.text.length)
                                        ) {
                                          uniqueReferences.set(documentName, {
                                            ...existing,
                                            content: reference.content,
                                          });
                                        }
                                      }
                                    }
                                  });
                                });

                                // Render unique references with citation numbers
                                return Array.from(uniqueReferences.entries()).map(
                                  ([documentName, reference], index) => {
                                    const citationNumber = index + 1;

                                    return (
                                      <div
                                        key={documentName}
                                        className="bg-secondary-100/30 rounded-lg p-2 border border-secondary-200/30 hover:bg-secondary-100/50 hover:border-secondary-300/40 hover:shadow-sm transition-all duration-200 ease-out cursor-pointer group animate-fadeInUp"
                                        style={{
                                          animationDelay: `${index * 150}ms`,
                                          animationFillMode: 'both',
                                        }}
                                      >
                                        <div className="flex items-center">
                                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center mr-2 shadow-sm group-hover:shadow-md group-hover:scale-110 transition-all duration-300 ease-out">
                                            <span className="text-xs font-bold">{citationNumber}</span>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="text-sm font-semibold text-secondary-900 leading-snug tracking-tight">
                                              {hasDocumentUrl(reference.documentName) ? (
                                                <button
                                                  onClick={() => openDocument(reference.documentName)}
                                                  className="text-primary-600 hover:text-primary-700 focus:text-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:ring-offset-2 rounded-md cursor-pointer transition-all duration-300 text-left flex items-center gap-1.5 group/btn p-1.5 -m-1.5 hover:bg-primary-50/50 min-h-[36px] w-full"
                                                  title={`Open ${reference.documentName} in new tab`}
                                                  aria-label={`Open document: ${reference.documentName} in new tab`}
                                                  type="button"
                                                >
                                                  <span className="group-hover/btn:underline decoration-2 underline-offset-2 font-medium">
                                                    {reference.documentName}
                                                  </span>
                                                  <svg
                                                    className="w-3.5 h-3.5 opacity-60 group-hover/btn:opacity-100 flex-shrink-0 transition-all duration-200 ease-out"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    aria-hidden="true"
                                                  >
                                                    <path
                                                      strokeLinecap="round"
                                                      strokeLinejoin="round"
                                                      strokeWidth={2}
                                                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                                    />
                                                  </svg>
                                                </button>
                                              ) : (
                                                <span className="text-secondary-800">{reference.documentName}</span>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  },
                                );
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Resources */}
                        {message.resources && message.resources.length > 0 && (
                          <div
                            className={`bg-accent-50/80 rounded-lg p-4 border border-accent-300/80 shadow-sm ${
                              message.citations && message.citations.length > 0 ? 'mt-3' : ''
                            }`}
                          >
                            <div className="text-sm font-semibold text-accent-700 mb-3 flex items-center">
                              <FiTarget className="h-4 w-4 mr-2" />
                              Resources
                            </div>
                            <div className="space-y-2">
                              {message.resources.map((resource, index) => (
                                <div
                                  key={index}
                                  className="bg-accent-50/40 rounded-md p-2.5 border border-accent-200/30 hover:bg-accent-50/60 hover:border-accent-300/40 transition-all duration-200"
                                >
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-accent-100 text-accent-600 flex items-center justify-center mr-2">
                                      {getResourceIcon(resource.type)}
                                    </div>
                                    <a
                                      href={resource.link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs font-medium text-primary-600 hover:text-primary-800 hover:underline transition-colors flex-1"
                                    >
                                      {resource.title}
                                    </a>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                </div>
              ))}
              <div ref={messagesEndRef} />

              {/* Prompt suggestions - only show for the first assistant message */}
              {messages.length > 0 &&
                messages[messages.length - 1].role === 'assistant' &&
                !isLoading &&
                // Only show suggestions if there's only one user message or none (welcome message only)
                messages.filter((msg) => msg.role === 'user').length < 1 && (
                  <div className="px-4 py-2 mb-2">
                    {/* Generic prompt suggestions */}
                    {!selectedCustomerId && (
                      <div className="mb-2">
                        <div className="text-xs text-secondary-500 mb-1">Try asking:</div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            className="text-sm text-white hover:text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-full px-4 py-2 font-semibold shadow-md transform hover:scale-105 transition-all duration-200"
                            onClick={() =>
                              handlePromptSuggestionClick("What's new in Kiro in the last 90 days?")
                            }
                          >
                            🚀 What's new in Kiro in the last 90 days?
                          </button>
                          <span className="text-secondary-300">•</span>
                          <button
                            className="text-xs text-primary-600 hover:text-primary-800 hover:underline text-left"
                            onClick={() =>
                              handlePromptSuggestionClick(
                                'What are the prerequisites for running a Kiro workshop?',
                              )
                            }
                          >
                            What are the prerequisites for running a Kiro workshop?
                          </button>
                          <span className="text-secondary-300">•</span>
                          <button
                            className="text-xs text-primary-600 hover:text-primary-800 hover:underline text-left"
                            onClick={() =>
                              handlePromptSuggestionClick(
                                'What security requirements must be met for Kiro approval?',
                              )
                            }
                          >
                            What security requirements must be met for Kiro approval?
                          </button>
                          <span className="text-secondary-300">•</span>
                          <button
                            className="text-xs text-primary-600 hover:text-primary-800 hover:underline text-left"
                            onClick={() =>
                              handlePromptSuggestionClick('How can I help my customer adopt Kiro effectively?')
                            }
                          >
                            How can I help my customer adopt Kiro effectively?
                          </button>
                          <span className="text-secondary-300">•</span>
                          <button
                            className="text-xs text-primary-600 hover:text-primary-800 hover:underline text-left"
                            onClick={() =>
                              handlePromptSuggestionClick(
                                'What metrics should I suggest my customer to track for Kiro success?',
                              )
                            }
                          >
                            What metrics should I suggest my customer to track for Kiro success?
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Customer-specific prompt suggestions */}
                    {selectedCustomerName && (
                      <div className="mb-2">
                        <div className="text-xs text-secondary-500 mb-1">Try asking about {selectedCustomerName}:</div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            className="text-sm text-white hover:text-white bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 rounded-full px-4 py-2 font-semibold shadow-md transform hover:scale-105 transition-all duration-200"
                            onClick={() =>
                              handlePromptSuggestionClick(
                                `What new Kiro features should ${selectedCustomerName} adopt to improve their development workflow?`,
                              )
                            }
                          >
                            🚀 New Kiro features {selectedCustomerName} should adopt
                          </button>
                          <span className="text-secondary-300">•</span>
                          <button
                            className="text-xs text-primary-600 hover:text-primary-800 hover:underline text-left"
                            onClick={() =>
                              handlePromptSuggestionClick(
                                `What trainings have been provided to ${selectedCustomerName} so far?`,
                              )
                            }
                          >
                            What trainings have been provided to {selectedCustomerName} so far?
                          </button>
                          <span className="text-secondary-300">•</span>
                          <button
                            className="text-xs text-primary-600 hover:text-primary-800 hover:underline text-left"
                            onClick={() =>
                              handlePromptSuggestionClick(
                                `What issues has ${selectedCustomerName} reported with Kiro?`,
                              )
                            }
                          >
                            What issues has {selectedCustomerName} reported with Kiro?
                          </button>
                          <span className="text-secondary-300">•</span>
                          <button
                            className="text-xs text-primary-600 hover:text-primary-800 hover:underline text-left"
                            onClick={() =>
                              handlePromptSuggestionClick(
                                `What customizations has ${selectedCustomerName} implemented?`,
                              )
                            }
                          >
                            What customizations has {selectedCustomerName} implemented?
                          </button>
                          <span className="text-secondary-300">•</span>
                          <button
                            className="text-xs text-primary-600 hover:text-primary-800 hover:underline text-left"
                            onClick={() =>
                              handlePromptSuggestionClick(
                                `What are the key learnings from ${selectedCustomerName}'s implementation?`,
                              )
                            }
                          >
                            What are the key learnings from {selectedCustomerName}'s implementation?
                          </button>
                          <span className="text-secondary-300">•</span>
                          <button
                            className="text-xs text-primary-600 hover:text-primary-800 hover:underline text-left"
                            onClick={() =>
                              handlePromptSuggestionClick(
                                `What specific use cases is ${selectedCustomerName} implementing?`,
                              )
                            }
                          >
                            What specific use cases is {selectedCustomerName} implementing?
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

              {/* Loading indicator */}
              {isLoading && (
                <div className="flex justify-start animate-pulse">
                  <div className="bg-surface border border-secondary-200 rounded-xl p-3 flex items-center shadow-chat-bubble ml-1">
                    <div className="flex space-x-1">
                      <div
                        className="w-2 h-2 bg-primary-400 rounded-full animate-bounce"
                        style={{ animationDelay: '0ms' }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-primary-400 rounded-full animate-bounce"
                        style={{ animationDelay: '150ms' }}
                      ></div>
                      <div
                        className="w-2 h-2 bg-primary-400 rounded-full animate-bounce"
                        style={{ animationDelay: '300ms' }}
                      ></div>
                    </div>
                    <span className="ml-2 text-sm text-secondary-600">Thinking</span>
                  </div>
                </div>
              )}
            </div>

            {/* Input area */}
            <div className="p-4 border-t border-secondary-200/60 bg-white backdrop-blur-sm">
              {/* Customer selector row */}
              <div className="mb-3">
                <CustomerProvider>
                  {/* CustomerSelectorForChat temporarily disabled for compilation */}
                  {/* <CustomerSelectorForChat
                    onCustomerSelect={(customer: CustomerBrowseItem | null) => {
                      if (customer) {
                        setSelectedCustomerId(customer.customerId);
                        setSelectedCustomerName(customer.name);
                      } else {
                        setSelectedCustomerId(null);
                        setSelectedCustomerName(null);
                      }
                    }}
                  /> */}
                </CustomerProvider>
              </div>

              {/* Customer Selection */}
              <div className="mb-3">
                <div className="flex items-center space-x-3">
                  <label className="text-sm font-medium text-gray-700">Customer:</label>
                  <select
                    value={selectedCustomerId || ''}
                    onChange={(e) => {
                      const customerId = e.target.value || null;
                      const customer = availableCustomers.find((c) => c.customerId === customerId);
                      setSelectedCustomerId(customerId);
                      setSelectedCustomerName(customer?.name || null);
                      // Clear not found message when user manually selects
                      setCustomerNotFoundMessage(null);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loadingCustomers}
                  >
                    <option value="">{loadingCustomers ? 'Loading customers...' : 'No Customer Selected'}</option>
                    {availableCustomers.map((customer) => (
                      <option key={customer.customerId} value={customer.customerId}>
                        {customer.name}
                      </option>
                    ))}
                  </select>
                  {selectedCustomerId && <span className="text-xs text-green-600">✓ Selected</span>}
                </div>
                {/* Customer not found message */}
                {customerNotFoundMessage && (
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-800">⚠️ {customerNotFoundMessage}</p>
                  </div>
                )}
              </div>

              <div className="flex items-end">
                <div className="flex-1 relative">
                  <textarea
                    className="w-full p-3 pr-10 border border-secondary-300/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-300 resize-none bg-white/90 backdrop-blur-sm shadow-sm hover:shadow-md transition-all duration-200"
                    placeholder="Type your message..."
                    rows={2}
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading}
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-secondary-400">
                    {input.length > 0 && `${input.length} chars`}
                  </div>
                </div>
                <button
                  className="ml-2 p-3 h-12 w-12 flex items-center justify-center bg-primary-600 text-white rounded-full hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-chat-bubble"
                  onClick={handleSendMessage}
                  disabled={input.trim() === '' || isLoading}
                  aria-label="Send message"
                >
                  {isLoading ? <FiRefreshCw className="animate-spin" /> : <FiSend />}
                </button>
              </div>
              <div className="flex justify-between items-center mt-2">
                <div className="text-xs text-secondary-500">Press Enter to send, Shift+Enter for a new line</div>
                <p className="text-xs text-secondary-500">Powered by Amazon Bedrock</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
